import React, { useState } from "react";
import { toast } from "react-toastify";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import Input from "../ui/Input";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as tenantDepartmentActions from "src/redux/slices/tenantDepartmentSlice";
import { TenantDepartment } from "src/types/tenantDepartment.types";

interface AddTenantDepartmentModalProps {
  department: TenantDepartment | null;
  onClose: () => void;
  onSuccess: () => void;
}

const AddTenantDepartmentModal: React.FC<AddTenantDepartmentModalProps> = ({
  department,
  onClose,
  onSuccess,
}) => {
  const isEdit = !!department?._id;
  const [name, setName] = useState(department?.name || "");
  const [touched, setTouched] = useState(false);
  const dispatch = useAppDispatch();
  const { createTenantDepartment, editTenantDepartment } = useAppSelector(
    (state) => state.tenantDepartment
  );
  const loading = isEdit
    ? editTenantDepartment.loading
    : createTenantDepartment.loading;

  React.useEffect(() => {
    if (isEdit && editTenantDepartment.data) {
      toast.success("Department updated successfully");
      dispatch(tenantDepartmentActions.resetEditTenantDepartment());
      onSuccess();
    } else if (isEdit && editTenantDepartment.error) {
      toast.error(`Error: ${editTenantDepartment.error}`);
      dispatch(tenantDepartmentActions.resetEditTenantDepartment());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editTenantDepartment]);

  React.useEffect(() => {
    if (!isEdit && createTenantDepartment.data) {
      toast.success("Department created successfully");
      dispatch(tenantDepartmentActions.resetCreateTenantDepartment());
      onSuccess();
    } else if (!isEdit && createTenantDepartment.error) {
      toast.error(`Error: ${createTenantDepartment.error}`);
      dispatch(tenantDepartmentActions.resetCreateTenantDepartment());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createTenantDepartment]);

  const handleSubmit = () => {
    setTouched(true);
    if (!name.trim()) return;
    if (isEdit) {
      dispatch(
        tenantDepartmentActions.editTenantDepartmentRequest({
          _id: department!._id,
          name: name.trim(),
        })
      );
    } else {
      dispatch(
        tenantDepartmentActions.createTenantDepartmentRequest({
          name: name.trim(),
        })
      );
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? "Edit department" : "Add department"}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} isLoading={loading} disabled={loading}>
            {isEdit ? "Save changes" : "Add department"}
          </Button>
        </>
      }
    >
      <Input
        label="Department name"
        placeholder="e.g. Sales, Engineering"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
        error={touched && !name.trim()}
        helperText={
          touched && !name.trim() ? "Department name is required" : undefined
        }
      />
    </Modal>
  );
};

export default AddTenantDepartmentModal;
